/**
 * ═══ OÙ EST LA PHOTO DANS SON CADRE ═══
 *
 * La visionneuse loge la photo dans un cadre aux dimensions de la fenêtre, en
 * `object-fit: contain` : la photo est la plus grande possible sans être rognée, et ce qui
 * reste du cadre est sombre. Le fond ferme la visionneuse — c'est la règle du site — et
 * la zone sombre À CÔTÉ de la photo doit fermer aussi : à l'œil, c'est le fond.
 *
 * ⚠️ MAIS CE N'EST PAS LE FOND. Avec `object-fit`, la boîte de l'image est le cadre entier ;
 * la part sombre lui appartient, et aucun `pointer-events` ne sépare une boîte d'elle-même.
 * Le seul moyen de savoir où le clic est tombé est de refaire le calcul du navigateur :
 * la photo, réduite au même rapport dans les deux sens et centrée, occupe un rectangle
 * connu dès qu'on connaît le cadre et les dimensions naturelles de l'image.
 *
 * Pur, sans DOM : ce qui se teste ici est l'arithmétique, pas le clic.
 */

export type Box = { x: number; y: number; width: number; height: number };
export type Size = { width: number; height: number };
export type Point = { x: number; y: number };

/**
 * Le rectangle qu'occupe une image `contain` dans son cadre — ou `null` quand ses
 * dimensions ne sont pas connues (pas encore chargée) : on ne devine pas.
 */
export function visiblePicture(frame: Box, natural: Size): Box | null {
  if (natural.width <= 0 || natural.height <= 0) return null;
  if (frame.width <= 0 || frame.height <= 0) return null;
  const k = Math.min(frame.width / natural.width, frame.height / natural.height);
  const width = natural.width * k;
  const height = natural.height * k;
  return {
    x: frame.x + (frame.width - width) / 2,
    y: frame.y + (frame.height - height) / 2,
    width,
    height,
  };
}

/**
 * Le clic est-il tombé À CÔTÉ de la photo, dans la part sombre du cadre ?
 *
 * ⚠️ FAUX QUAND ON NE SAIT PAS. Sans dimensions connues, tout le cadre pourrait être de la
 * photo : fermer sur un clic qui visait peut-être l'image serait pire que ne rien faire.
 */
export function isBeside(frame: Box, natural: Size, at: Point): boolean {
  const pic = visiblePicture(frame, natural);
  if (!pic) return false;
  const inside =
    at.x >= pic.x &&
    at.x <= pic.x + pic.width &&
    at.y >= pic.y &&
    at.y <= pic.y + pic.height;
  return !inside;
}
